import { ImageEntity } from "@client/content/controls/Image/container";
import { getRootContainerProps } from "@client/content/lib/helper";
import { ModuleButton } from "@client/content/modules/Button";
import { Alert } from "@client/controls/Alert/component";
import Heading, { type HeadingProps } from "@client/controls/HTML/Heading/component";
import NumberInput from "@client/controls/Inputs/NumberInput/component";
import Link from "@client/controls/Link/container";
import Rate from "@client/controls/Rate/component";
import { goTo } from "@client/lib/navigation";
import { IconType, SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { default as classnames } from "classnames";
import React from "react";
import { ProductList } from "../../types";
import { type ProductListItemContainerProps } from "./container";
import ProductPrice from "./ProductPrice";

export type MapProps = ProductListItemContainerProps & {
  addCaption: string;
  buyCaption: string;
  detailCaption: string;

  cartUrl: string;

  buttons: ProductList.Button[];
  elements: ProductList.Element[];

  style: React.CSSProperties;
  nameHeading: HeadingProps;

  imageContainerStyle: React.CSSProperties;
  contentStyle: React.CSSProperties;
  fieldStyle: React.CSSProperties;
  product: ProductList.Product;
  quantityStyle: React.CSSProperties;
  regularPriceStyle: React.CSSProperties;
  salePriceStyle: React.CSSProperties;
  oldPriceColor: string | undefined;
  outOfStockStyle: React.CSSProperties | undefined;
  shouldDisplayTax: boolean;

  noImageUrl: string;

  isAddingToCart: boolean;
};

export interface ActionProps {
  addToCart: (quantiy: number) => void;
  buyNow: (quantiy: number) => void;
}

export type ProductListItemProps = ActionProps & MapProps;
const ProductListItem = ({ product, buttons, elements, ...props }: ProductListItemProps) => {
  const i18n = useI18n();

  const [quantity, setQuantity] = React.useState(1);
  const onAddToCartClick = React.useCallback(() => props.addToCart(quantity), [props.addToCart, quantity]);
  const onByNowClick = React.useCallback(() => props.buyNow(quantity), [props.buyNow, quantity]);

  return (
    <div className={classnames("product-item")}>
      <div className="product-item-inner" style={props.style}>
        <div className="product-image" style={props.imageContainerStyle}>
          <Link to={product.permalink}>
            <ImageEntity
              id={props.parentId}
              source={props.source}
              src={product.imageUrl || props.noImageUrl}
              useMaxValues={false}
            />
          </Link>

          {/* {elements.includes("savings") && product.salesLabel && (
            <div className="sales-label">{product.salesLabel}</div>
          )} */}
        </div>
        <div className="product-description" style={props.contentStyle}>
          <Link to={product.permalink}>
            <Heading className="product-name" {...props.nameHeading} content={product.name} />
          </Link>

          {elements.includes("price") && (
            <ProductPrice
              oldPriceColor={props.oldPriceColor}
              price={product.price}
              regularPriceStyle={props.regularPriceStyle}
              salePriceStyle={props.salePriceStyle}
              taxPercentage={props.shouldDisplayTax ? product.taxPercentage : 0}
            />
          )}

          {product.allowReviews && <Rate className="product-rate" rate={product.rating ?? 0} />}

          <div className="bottom">
            {product.availableQuantity > 0 ? (
              <>
                {elements.includes("quantity") && !product.hasVariants && !!product.availableQuantity && (
                  <span className="product-quantity" style={props.quantityStyle}>
                    <span className="label">{i18n.quantity}</span>
                    <NumberInput
                      className="form-control"
                      min={1}
                      max={product.availableQuantity}
                      onChange={(v) => setQuantity(v)}
                      style={props.fieldStyle}
                      value={quantity}
                    />
                  </span>
                )}

                <div className="buttons">
                  {buttons.includes("detail") && (
                    <ModuleButton
                      {...getRootContainerProps(props)}
                      captionPropName="seeDetailsCaption"
                      captionPropPath=""
                      id={props.parentId}
                      onClick={() => goTo(product.permalink)}
                      caption={props.detailCaption}
                    />
                  )}
                  {!product.hasVariants && buttons.includes("cart") && !!product.availableQuantity && (
                    <ModuleButton
                      {...getRootContainerProps(props)}
                      captionPropName="addToCartCaption"
                      captionPropPath=""
                      id={props.parentId}
                      onClick={onAddToCartClick}
                      isDisabled={props.isAddingToCart}
                      caption={product.progressButton === "add" ? i18n.addingToCart : props.addCaption}
                    />
                  )}
                  {!product.hasVariants && buttons.includes("buy") && !!product.availableQuantity && (
                    <ModuleButton
                      {...getRootContainerProps(props)}
                      captionPropName="buyNowCaption"
                      captionPropPath=""
                      id={props.parentId}
                      onClick={onByNowClick}
                      isDisabled={props.isAddingToCart}
                      caption={product.progressButton === "buy" ? i18n.pleaseWait : props.buyCaption}
                    />
                  )}
                </div>
              </>
            ) : (
              <span className="out_of_stock" style={props.outOfStockStyle}>
                {i18n.outOfStock}
              </span>
            )}
          </div>

          {product.isInCart && (
            <Alert color={SystemColor.Green} icon={IconType.Check}>
              {i18n.added} <Link to={props.cartUrl}>{i18n.viewCart}</Link>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
};
export default ProductListItem;
